/**
 * Abertura de um itinerário Original no Google Maps — apenas por link.
 *
 * REGRA: nenhuma chamada à API do Google, nenhuma chave. Construímos
 * localmente URLs `https://www.google.com/maps/dir/?api=1`.
 *
 * Limites respeitados:
 *  - No máximo 3 pontos intermédios por etapa (5 locais: origem, 3, destino),
 *    o que funciona em browsers de telemóvel e na app do Maps.
 *  - No máximo 2048 caracteres por URL; se um segmento não couber, é dividido
 *    outra vez até caber.
 *  - A última paragem de uma etapa é sempre a primeira da etapa seguinte.
 *    Nenhuma paragem é omitida e a ordem nunca muda.
 *
 * O Google Maps pode recalcular o caminho entre paragens: isto abre o
 * itinerário no Maps, não garante o traçado exato.
 *
 * Módulo puro: sem IO, sem relógio, sem aleatoriedade.
 */

export const MAX_WAYPOINTS_PER_LEG = 3;
export const MAX_POINTS_PER_LEG = MAX_WAYPOINTS_PER_LEG + 2;
export const MAX_MAPS_URL_LENGTH = 2048;

export type OriginalTransportMode = "walking" | "driving" | "transit" | "bicycling";

export const TRANSPORT_MODE_LABEL: Record<OriginalTransportMode, string> = {
  walking: "a pé",
  driving: "de carro",
  transit: "de transportes públicos",
  bicycling: "de bicicleta",
};

/** Normaliza qualquer valor guardado para um modo aceite pelo Maps. */
export function normalizeTransportMode(value: unknown): OriginalTransportMode {
  const raw = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (raw === "driving" || raw === "car" || raw === "carro" || raw === "motorcycle") return "driving";
  if (raw === "transit" || raw === "train" || raw === "transportes" || raw === "publico") return "transit";
  if (raw === "bicycling" || raw === "bike" || raw === "bicicleta" || raw === "scooter") return "bicycling";
  return "walking";
}

export type DirectionsPoint = {
  name: string;
  placeId?: string;
  lat?: number;
  lng?: number;
};

export type DirectionsLeg = {
  /** 1..N */
  index: number;
  url: string;
  /** Nome do primeiro local da etapa. */
  from: string;
  /** Nome do último local da etapa. */
  to: string;
  /** Posição (1-based) da primeira paragem da etapa na lista completa. */
  firstStopNumber: number;
  /** Posição (1-based) da última paragem da etapa na lista completa. */
  lastStopNumber: number;
  /** Nomes dos locais desta etapa, pela ordem. */
  stopNames: string[];
};

function hasCoords(point: DirectionsPoint): boolean {
  return (
    typeof point.lat === "number" &&
    typeof point.lng === "number" &&
    Number.isFinite(point.lat) &&
    Number.isFinite(point.lng) &&
    point.lat >= -90 &&
    point.lat <= 90 &&
    point.lng >= -180 &&
    point.lng <= 180
  );
}

/** Coordenadas quando existem; caso contrário o nome do local. */
function pointValue(point: DirectionsPoint): string {
  if (hasCoords(point)) {
    return `${Number(point.lat)},${Number(point.lng)}`;
  }
  return point.name.trim();
}

/** Só entram pontos com algo utilizável no Maps. */
export function usableDirectionsPoints(points: DirectionsPoint[]): DirectionsPoint[] {
  return points.filter((p) => hasCoords(p) || (p.name ?? "").trim().length > 0);
}

function buildLegUrl(points: DirectionsPoint[], mode: OriginalTransportMode): string {
  const origin = points[0]!;
  const destination = points[points.length - 1]!;
  const middle = points.slice(1, -1);
  const params = new URLSearchParams({
    api: "1",
    origin: pointValue(origin),
    destination: pointValue(destination),
    travelmode: mode,
  });
  if (origin.placeId) params.set("origin_place_id", origin.placeId);
  if (destination.placeId) params.set("destination_place_id", destination.placeId);
  if (middle.length > 0) {
    params.set("waypoints", middle.map(pointValue).join("|"));
  }
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

/**
 * Divide a lista em blocos sobrepostos: cada bloco começa no último ponto do
 * anterior. Blocos demasiado longos para o URL são divididos outra vez.
 */
function splitIntoLegPoints(points: DirectionsPoint[], mode: OriginalTransportMode): DirectionsPoint[][] {
  const legs: DirectionsPoint[][] = [];
  let start = 0;
  while (start < points.length - 1) {
    let size = Math.min(MAX_POINTS_PER_LEG, points.length - start);
    let chunk = points.slice(start, start + size);
    // Garante o limite de caracteres do URL, encurtando a etapa se preciso.
    while (chunk.length > 2 && buildLegUrl(chunk, mode).length > MAX_MAPS_URL_LENGTH) {
      size -= 1;
      chunk = points.slice(start, start + size);
    }
    legs.push(chunk);
    start += chunk.length - 1; // sobreposição: o último é o primeiro do próximo
  }
  return legs;
}

/**
 * Constrói os links "Abrir rota no Google Maps" (ou "Abrir etapa N") de uma
 * sequência ordenada de paragens.
 */
export function buildOriginalDirectionsLegs(
  stops: DirectionsPoint[],
  mode: OriginalTransportMode = "walking",
): DirectionsLeg[] {
  const points = usableDirectionsPoints(stops);
  if (points.length < 2) return [];
  const chunks = splitIntoLegPoints(points, mode);
  let consumed = 0;
  return chunks.map((chunk, index) => {
    const firstStopNumber = consumed + 1;
    const lastStopNumber = consumed + chunk.length;
    consumed += chunk.length - 1;
    return {
      index: index + 1,
      url: buildLegUrl(chunk, mode),
      from: chunk[0]!.name,
      to: chunk[chunk.length - 1]!.name,
      firstStopNumber,
      lastStopNumber,
      stopNames: chunk.map((p) => p.name),
    };
  });
}

/** Texto do botão de cada etapa. */
export function legButtonLabel(leg: DirectionsLeg, totalLegs: number): string {
  return totalLegs === 1 ? "Abrir rota no Google Maps" : `Abrir etapa ${leg.index}`;
}

/** Legenda com o intervalo de paragens da etapa. */
export function legRangeLabel(leg: DirectionsLeg): string {
  return `Paragens ${leg.firstStopNumber} a ${leg.lastStopNumber}`;
}
